import {
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryArgs,
    FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { getAdfsAccessToken, revalidateAdfsSession } from "@/services/auth/adfs.service";
import { getEntraIdAccessToken, refreshEntraIdSession } from "@/services/auth/entra-id.service";
import { authenticate, logout } from "@/store/slices/auth.slice";
import type { RootState } from "@/store/store";
import { getImpersonationToken } from "@/utils/impersonation.utils";

export const prepareHeaders = async (headers: Headers, api: Pick<BaseQueryApi, "getState">) => {
    const state = api.getState() as RootState;
    const authStrategy = state.auth.authStrategy;

    let token: string | null = null;
    if (authStrategy === "adfs") {
        token = await getAdfsAccessToken();
    } else if (authStrategy === "entraId") {
        token = await getEntraIdAccessToken();
    }

    if (token) {
        headers.set("authorization", `Bearer ${token}`);
    }

    const impersonationToken = getImpersonationToken();
    if (impersonationToken) {
        headers.set("Dsa-Impersonation", impersonationToken);
    }

    return headers;
};

const mutex = new Mutex();

const lockMutexAndRevalidate = async ({ dispatch, getState }: Pick<BaseQueryApi, "dispatch" | "getState">) => {
    const release = await mutex.acquire();
    const state = getState() as RootState;
    const { authStrategy } = state.auth;
    try {
        if (authStrategy === "adfs") {
            const authData = await revalidateAdfsSession();
            dispatch(authenticate({ authStrategy: "adfs", authData }));
        } else if (authStrategy === "entraId") {
            await refreshEntraIdSession();
            dispatch(authenticate({ authStrategy: "entraId" }));
        } else {
            dispatch(logout());
        }
    } catch {
        dispatch(logout());
    } finally {
        release();
    }
};

/**
 * Extend fetchBaseQuery which automatically tries to acquire a new accessToken when 401 error is received.
 * with using async-mutex to prevent multiple acquiringTokens attempt when multiple calls fail with 401 Unauthorized errors.
 * source: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
 * @param args
 * @param api
 * @param extraOptions
 */
const createBaseQueryWithAuthRefresh = (baseQueryArgs: FetchBaseQueryArgs) => {
    const baseQuery = fetchBaseQuery(baseQueryArgs);

    const baseQueryWithAuthRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
        args,
        api,
        extraOptions,
    ) => {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();
        let result = await baseQuery(args, api, extraOptions);
        if (!result.error || result.error.status !== 401) {
            return result;
        }

        if (!mutex.isLocked()) {
            await lockMutexAndRevalidate(api);
        } else {
            await mutex.waitForUnlock();
        }

        result = await baseQuery(args, api, extraOptions);
        return result;
    };

    return baseQueryWithAuthRefresh;
};

export { createBaseQueryWithAuthRefresh };
